/**
 * businessAiApi.js
 *
 * Thin client for the Happy AI business endpoints: the daily/weekly digest
 * (`/ai/business-digest`), anomaly alerts (`/ai/business-alerts`) and business
 * goals (`/ai/business-goals`). Cloned from `helpers/happyAiApi.js`: every
 * export normalizes to `{ok, data, error}` and NEVER throws, so screens can
 * await these directly in a render effect without a try/catch of their own.
 *
 * @module helpers/businessAiApi
 */
import {apiFetch, apiRequest} from './WorkSheetContext';

/**
 * Scope headers that accompany a call (mirrors `happyAiApi`'s `scopeHeaders`).
 * @param {{restaurantId?: string}} [scope] the active management scope.
 * @returns {Record<string, string>} headers to merge into an `apiFetch` options object.
 */
const scopeHeaders = (scope) => (scope?.restaurantId ? {'x-restaurant-id': String(scope.restaurantId)} : {});

/**
 * Build a query string, skipping empty values.
 * @param {Record<string, *>} params raw params.
 * @param {{restaurantId?: string}} [scope] active scope (adds `restaurantId`).
 * @returns {string} `''` or `?a=b&c=d`.
 */
const buildQuery = (params, scope) => {
    const query = new globalThis.URLSearchParams();

    if (scope?.restaurantId) query.set('restaurantId', String(scope.restaurantId));
    Object.keys(params || {}).forEach((key) => {
        const value = params[key];

        if (value === undefined || value === null || value === '' || value === false) return;
        query.set(key, String(value));
    });
    const text = query.toString();

    return text ? `?${text}` : '';
};

/**
 * Run one request and normalize the result.
 * @param {string} path API path (no host).
 * @param {object} options `apiFetch` options.
 * @param {string} fallbackError error code used when the thrown error has no message.
 * @returns {Promise<{ok: boolean, data: (object|null), error: (string|null)}>}
 */
const run = async (path, options, fallbackError) => {
    try {
        const data = await apiFetch(path, options);

        return {ok: true, data: data || null, error: null};
    } catch (error) {
        return {ok: false, data: null, error: error?.message || fallbackError};
    }
};

const jsonOptions = (method, body, scope) => ({
    method,
    headers: {'Content-Type': 'application/json', ...scopeHeaders(scope)},
    body: JSON.stringify(body || {}),
});

/* ----------------------------- digest ----------------------------------- */

/**
 * Fetch the digest for a day/week.
 * @param {{period?: 'day'|'week', date?: string, locale?: string, refresh?: boolean}} [params] request params.
 * @param {{restaurantId?: string}} [scope] active management scope.
 * @returns {Promise<{ok: boolean, data: (object|null), error: (string|null)}>}
 */
export const getDigest = ({period = 'day', date, locale, refresh = false} = {}, scope) => run(
    `/ai/business-digest${buildQuery({period, date, locale, refresh}, scope)}`,
    {headers: scopeHeaders(scope), ...(refresh ? {skipCache: true} : {})},
    'business_digest_failed',
);

/**
 * Fetch the digest history list.
 * @param {{period?: 'day'|'week', limit?: number}} [params] request params.
 * @param {{restaurantId?: string}} [scope] active management scope.
 * @returns {Promise<{ok: boolean, data: (object|null), error: (string|null)}>}
 */
export const getDigestHistory = ({period = 'day', limit = 14} = {}, scope) => run(
    `/ai/business-digest/history${buildQuery({period, limit}, scope)}`,
    {headers: scopeHeaders(scope)},
    'business_digest_history_failed',
);

/**
 * Fetch the digest delivery settings.
 * @param {{restaurantId?: string}} [scope] active management scope.
 * @returns {Promise<{ok: boolean, data: (object|null), error: (string|null)}>}
 */
export const getDigestSettings = (scope) => run(
    `/ai/business-digest/settings${buildQuery({}, scope)}`,
    {headers: scopeHeaders(scope), skipCache: true},
    'business_digest_settings_failed',
);

/**
 * Save the digest delivery settings.
 * @param {object} settings `{enabled, dailyEnabled, weeklyEnabled, sendTimeLocal, channels, alertsEnabled, timezone}`.
 * @param {{restaurantId?: string}} [scope] active management scope.
 * @returns {Promise<{ok: boolean, data: (object|null), error: (string|null)}>}
 */
export const saveDigestSettings = (settings, scope) => run(
    `/ai/business-digest/settings${buildQuery({}, scope)}`,
    jsonOptions('PUT', settings, scope),
    'business_digest_settings_save_failed',
);

/* ----------------------------- alerts ----------------------------------- */

/**
 * List alerts.
 * @param {{status?: string, limit?: number}} [params] request params.
 * @param {{restaurantId?: string}} [scope] active management scope.
 * @returns {Promise<{ok: boolean, data: (object|null), error: (string|null)}>} `data` is `{items, openCount}`.
 */
export const getAlerts = ({status = 'open', limit = 50} = {}, scope) => run(
    `/ai/business-alerts${buildQuery({status, limit}, scope)}`,
    {headers: scopeHeaders(scope), skipCache: true},
    'business_alerts_failed',
);

/**
 * Open alert count (dashboard badge).
 * @param {{restaurantId?: string}} [scope] active management scope.
 * @returns {Promise<{ok: boolean, data: (object|null), error: (string|null)}>} `data` is `{openCount}`.
 */
export const getAlertCount = (scope) => run(
    `/ai/business-alerts/count${buildQuery({}, scope)}`,
    {headers: scopeHeaders(scope), skipCache: true},
    'business_alert_count_failed',
);

/**
 * Change an alert's status.
 * @param {string} alertId alert id.
 * @param {'open'|'acknowledged'|'dismissed'} status next status.
 * @param {{restaurantId?: string}} [scope] active management scope.
 * @returns {Promise<{ok: boolean, data: (object|null), error: (string|null)}>}
 */
export const updateAlertStatus = (alertId, status, scope) => run(
    `/ai/business-alerts/${encodeURIComponent(alertId)}`,
    jsonOptions('PATCH', {status}, scope),
    'business_alert_update_failed',
);

/* ----------------------------- goals ------------------------------------ */

/**
 * List goals with progress.
 * @param {{status?: string}} [params] request params.
 * @param {{restaurantId?: string}} [scope] active management scope.
 * @returns {Promise<{ok: boolean, data: (object|null), error: (string|null)}>} `data` is `{items}`.
 */
export const getGoals = ({status} = {}, scope) => run(
    `/ai/business-goals${buildQuery({status}, scope)}`,
    {headers: scopeHeaders(scope), skipCache: true},
    'business_goals_failed',
);

/**
 * Fetch one goal with full progress (incl. series).
 * @param {string} goalId goal id.
 * @param {{restaurantId?: string}} [scope] active management scope.
 * @returns {Promise<{ok: boolean, data: (object|null), error: (string|null)}>}
 */
export const getGoal = (goalId, scope) => run(
    `/ai/business-goals/${encodeURIComponent(goalId)}${buildQuery({}, scope)}`,
    {headers: scopeHeaders(scope), skipCache: true},
    'business_goal_failed',
);

/**
 * Create a goal.
 * @param {{title: string, metric: string, period: string, from?: string, to?: string, target: number}} goal goal fields.
 * @param {{restaurantId?: string}} [scope] active management scope.
 * @returns {Promise<{ok: boolean, data: (object|null), error: (string|null)}>}
 */
export const createGoal = (goal, scope) => run(
    '/ai/business-goals',
    jsonOptions('POST', {...goal, ...(scope?.restaurantId ? {restaurantId: String(scope.restaurantId)} : {})}, scope),
    'business_goal_create_failed',
);

/**
 * Update a goal (title / target / status).
 * @param {string} goalId goal id.
 * @param {{title?: string, target?: number, status?: string}} patch changed fields.
 * @param {{restaurantId?: string}} [scope] active management scope.
 * @returns {Promise<{ok: boolean, data: (object|null), error: (string|null)}>}
 */
export const updateGoal = (goalId, patch, scope) => run(
    `/ai/business-goals/${encodeURIComponent(goalId)}`,
    jsonOptions('PUT', patch, scope),
    'business_goal_update_failed',
);

/**
 * Delete a goal.
 * @param {string} goalId goal id.
 * @param {{restaurantId?: string}} [scope] active management scope.
 * @returns {Promise<{ok: boolean, data: (object|null), error: (string|null)}>}
 */
export const deleteGoal = async (goalId, scope) => {
    try {
        // DELETE may answer 204 No Content; `apiFetch` would throw parsing the empty body.
        await apiRequest(`/ai/business-goals/${encodeURIComponent(goalId)}`, {method: 'DELETE', headers: scopeHeaders(scope)});

        return {ok: true, data: null, error: null};
    } catch (error) {
        return {ok: false, data: null, error: error?.message || 'business_goal_delete_failed'};
    }
};
